import { GitBranch, Plus, Trash2, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FdeEditorPanel } from '@/frontend/features/Fde/FdeEditorSurface'
import type { Evidence, FdeCopy, ProcessEdgeDefinition, ProcessReversibility, ProcessTransitionKind } from '@/frontend/features/Fde/types'

interface Props {
  edge: ProcessEdgeDefinition
  evidences: Evidence[]
  copy: FdeCopy
  onChange: (edge: ProcessEdgeDefinition) => void
  onDelete: () => void
  onClose: () => void
}

const transitionKinds: ProcessTransitionKind[] = ['sequence', 'conditional', 'exception', 'handoff', 'return', 'retry', 'cancel']
const reversibilityOptions: ProcessReversibility[] = ['unknown', 'reversible', 'irreversible']

export default function ProcessEdgeInspector(props: Props) {
  const [evidenceId, setEvidenceId] = useState('')
  const available = props.evidences.filter((evidence) => !props.edge.evidence_ids.includes(String(evidence.id)))

  function addEvidence() {
    if (!evidenceId) return
    props.onChange({ ...props.edge, evidence_ids: [...props.edge.evidence_ids, evidenceId] })
    setEvidenceId('')
  }

  return (
    <FdeEditorPanel
      title={props.copy.workspace.process_connection_detail}
      icon={<GitBranch />}
      closeLabel={props.copy.common.close}
      onClose={props.onClose}
      bodyClassName="space-y-7"
      testId="process-edge-editor"
      footer={(
        <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={props.onDelete}>
          <Trash2 />{props.copy.workspace.process_delete_connection}
        </Button>
      )}
    >
      <div className="grid min-w-0 gap-5 sm:grid-cols-2">
        <Field label={props.copy.workspace.process_transition_kind}>
          <Select value={props.edge.transition_kind || 'sequence'} onValueChange={(value) => props.onChange({ ...props.edge, transition_kind: value as ProcessTransitionKind })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>{transitionKinds.map((kind) => <SelectItem key={kind} value={kind}>{props.copy.workspace[`process_transition_${kind}`]}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={props.copy.workspace.process_reversibility}>
          <Select value={props.edge.reversibility || 'unknown'} onValueChange={(value) => props.onChange({ ...props.edge, reversibility: value as ProcessReversibility })}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>{reversibilityOptions.map((value) => <SelectItem key={value} value={value}>{props.copy.workspace[`process_reversibility_${value}`]}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field className="sm:col-span-2" label={props.copy.workspace.process_connection_label}>
          <Input value={props.edge.label || ''} onChange={(event) => props.onChange({ ...props.edge, label: event.target.value })} />
        </Field>
        <Field className="sm:col-span-2" label={props.copy.workspace.process_connection_condition}>
          <Input value={props.edge.condition || ''} onChange={(event) => props.onChange({ ...props.edge, condition: event.target.value })} />
        </Field>
      </div>

      <section className="grid min-w-0 gap-3 rounded-xl border border-border bg-muted/10 p-4">
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{props.copy.workspace.process_evidences}</h4>
        {props.edge.evidence_ids.length > 0 && <div className="space-y-2">{props.edge.evidence_ids.map((id) => (
          <div key={id} className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs">
            <span className="min-w-0 flex-1 truncate">{props.evidences.find((evidence) => String(evidence.id) === id)?.title || id}</span>
            <button type="button" onClick={() => props.onChange({ ...props.edge, evidence_ids: props.edge.evidence_ids.filter((candidate) => candidate !== id) })}><X className="size-3" /></button>
          </div>
        ))}</div>}
        <div className="flex min-w-0 gap-2">
          <Select value={evidenceId} onValueChange={setEvidenceId}><SelectTrigger className="min-w-0 flex-1"><SelectValue placeholder={props.copy.workspace.process_select_evidence} /></SelectTrigger><SelectContent>{available.map((evidence) => <SelectItem key={evidence.id} value={String(evidence.id)}>{evidence.title}</SelectItem>)}</SelectContent></Select>
          <Button size="icon" variant="outline" disabled={!evidenceId} onClick={addEvidence}><Plus /></Button>
        </div>
      </section>
    </FdeEditorPanel>
  )
}

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return <label className={`grid min-w-0 gap-1.5 text-xs font-medium text-muted-foreground ${className || ''}`}><span>{label}</span>{children}</label>
}
